import { useState, useEffect, useRef } from "react";
import { DatePicker, Space, Typography } from "antd";
import moment from "moment";
import { admin_dashboardApi, get_ai_usage_stats } from "../api/admin.apis";
import { getWorkspaceStatsOverviewApi, getWorkspaceActivationFunnelApi } from "../api/admin.workspaces.api";
import { WorkspaceStatsSection } from "../components/home/WorkspaceStatsSection";
import { DashboardCards } from "./../components/home/DashboardCards";
import { ChartsSection } from "./../components/home/ChartsSection";
import { LoadingState } from "./../components/home/LoadingState";
import { ErrorState } from "./../components/home/ErrorState";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

function startOfWeekMonday(value) {
  const day = value.day();
  const diff = day === 0 ? 6 : day - 1;
  return value.clone().startOf("day").subtract(diff, "days");
}

function currentWeekRange() {
  return [startOfWeekMonday(moment()), moment().endOf("day")];
}

function formatRangeLabel(range) {
  const [start, end] = range;
  if (start.year() === end.year()) {
    return `${start.format("MMM D")} – ${end.format("MMM D")}`;
  }
  return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
}

function Home() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiUsageData, setAiUsageData] = useState(null);
  const [aiUsageClass, setAiUsageClass] = useState("customer");
  const [aiLoading, setAiLoading] = useState(true);
  const [aiError, setAiError] = useState(null);
  const [showAiOverallDetails, setShowAiOverallDetails] = useState(false);
  const [showAiProviderDetails, setShowAiProviderDetails] = useState(false);
  const [showAiModelDetails, setShowAiModelDetails] = useState(false);
  const [showAiFeatureDetails, setShowAiFeatureDetails] = useState(false);
  const [showAiInternalDetails, setShowAiInternalDetails] = useState(false);
  const [showAiCustomerDetails, setShowAiCustomerDetails] = useState(false);
  const [workspaceStats, setWorkspaceStats] = useState(null);
  const [workspaceFunnel, setWorkspaceFunnel] = useState(null);
  const [workspaceStatsError, setWorkspaceStatsError] = useState(null);
  const [workspaceStatsLoading, setWorkspaceStatsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(currentWeekRange);
  const aiRequest = useRef(0);

  useEffect(() => {
    Promise.all([fetchDashboardData(), fetchWorkspaceStats()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAiUsageStats(aiUsageClass, dateRange);
  }, [aiUsageClass, dateRange]);

  const fetchWorkspaceStats = async () => {
    try {
      setWorkspaceStatsLoading(true);
      const [overviewRes, funnelRes] = await Promise.all([
        getWorkspaceStatsOverviewApi(),
        getWorkspaceActivationFunnelApi(),
      ]);
      if (overviewRes.data.status === "success") {
        setWorkspaceStats(overviewRes.data.data);
        setWorkspaceStatsError(null);
      } else {
        setWorkspaceStatsError(overviewRes.data.message || "Workspace stats unavailable");
      }
      if (funnelRes.data.status === "success") {
        setWorkspaceFunnel(funnelRes.data.data);
      }
    } catch (err) {
      setWorkspaceStatsError(err.message || "Workspace stats unavailable");
    } finally {
      setWorkspaceStatsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await admin_dashboardApi();
      if (response.data.status === "success") {
        setDashboardData(response.data.data);
      } else {
        setError("Failed to fetch dashboard data");
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "An error occurred");
    }
  };

  const fetchAiUsageStats = async (usageClass, range) => {
    const requestId = ++aiRequest.current;
    const [start, end] = range;
    setAiLoading(true);
    setAiError(null);
    try {
      const response = await get_ai_usage_stats({
        start_date: start.clone().startOf("day").toISOString(),
        end_date: end.clone().endOf("day").toISOString(),
        usage_class: usageClass,
      });
      if (requestId !== aiRequest.current) return;
      if (response.data.status === "success") {
        setAiUsageData(response.data.data);
      } else {
        setAiUsageData(null);
        setAiError(response.data.message || "AI usage unavailable");
      }
    } catch (err) {
      if (requestId !== aiRequest.current) return;
      console.error("Error fetching AI usage stats:", err);
      setAiUsageData(null);
      setAiError(err.message || "AI usage unavailable");
    } finally {
      if (requestId === aiRequest.current) setAiLoading(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchDashboardData} />;
  }

  const rangeLabel = formatRangeLabel(dateRange);

  return (
    <div className="layout-content dashboard-page">
      <style>{`
        .layout-dashboard .dashboard-page .ant-card {
          box-shadow: none;
        }
        .dashboard-page .ant-table-tbody > tr > td,
        .dashboard-page .ant-table-thead > tr > th {
          padding: 6px 10px;
        }
      `}</style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          padding: "16px 0 12px",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Dashboard
        </Title>
        <Space size={8} style={{ flexShrink: 0 }}>
          <Text type="secondary">Date range</Text>
          <RangePicker
            size="small"
            allowClear={false}
            value={dateRange}
            format="YYYY-MM-DD"
            onChange={(dates) => {
              if (dates?.[0] && dates?.[1]) setDateRange(dates);
            }}
            ranges={{
              "Current week": currentWeekRange(),
              "Last 7 days": [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
              "Last 30 days": [moment().subtract(29, "days").startOf("day"), moment().endOf("day")],
              "This month": [moment().startOf("month"), moment().endOf("day")],
            }}
          />
        </Space>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          Current
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Point-in-time. Does not follow the date range.
        </Text>
      </div>

      <WorkspaceStatsSection
        part="snapshot"
        stats={workspaceStats}
        funnel={workspaceFunnel}
        loading={workspaceStatsLoading}
        error={workspaceStatsError}
        dashboardData={dashboardData}
      />

      <div style={{ marginTop: 12 }}>
        <ChartsSection dateRange={dateRange} />
      </div>

      <DashboardCards
        aiUsageData={aiUsageData}
        aiUsageClass={aiUsageClass}
        onAiUsageClassChange={setAiUsageClass}
        aiWindowLabel={rangeLabel}
        aiLoading={aiLoading}
        aiError={aiError}
        showAiOverallDetails={showAiOverallDetails}
        setShowAiOverallDetails={setShowAiOverallDetails}
        showAiProviderDetails={showAiProviderDetails}
        setShowAiProviderDetails={setShowAiProviderDetails}
        showAiModelDetails={showAiModelDetails}
        setShowAiModelDetails={setShowAiModelDetails}
        showAiFeatureDetails={showAiFeatureDetails}
        setShowAiFeatureDetails={setShowAiFeatureDetails}
        showAiInternalDetails={showAiInternalDetails}
        setShowAiInternalDetails={setShowAiInternalDetails}
        showAiCustomerDetails={showAiCustomerDetails}
        setShowAiCustomerDetails={setShowAiCustomerDetails}
      />

      <WorkspaceStatsSection
        part="detail"
        stats={workspaceStats}
        funnel={workspaceFunnel}
        loading={workspaceStatsLoading}
        error={workspaceStatsError}
      />
    </div>
  );
}

export default Home;
